import React from 'react';
import { Outlet, useLocation } from '@tanstack/react-router';
import {
  Play,
  MagnifyingGlass,
  Stack,
  TestTube,
} from '@phosphor-icons/react';
import { useSubNav } from '../../../hooks/useSubNav';
import type { SubNavConfig } from '../../../contexts/SubNavContext';
import { isExploratoryPath } from '../../../features/exploratory-tests/flavors';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Shared layout for the `/runs/*` routes. These routes are now surfaced under
 * two sidebar sections:
 *   - "Labs" — QA Runs, feature-flag gated.
 *   - "Suites" — the test-run history (`/runs/test`), shown alongside the
 *     persisted suites at `/suites`.
 * The SubNav adapts to which group the current path belongs to so navigation
 * reads coherently regardless of entry point.
 */
export function RunsLayout() {
  const { pathname } = useLocation();
  // UX/Spec review runs live under /runs/* but belong to Exploratory, which has
  // no sub-nav.
  if (isExploratoryPath(pathname)) return <Outlet />;
  return <RunsSubNavLayout />;
}

function RunsSubNavLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const qaRunsEnabled = Boolean(user?.features?.qaRuns);
  const suitesEnabled = Boolean(user?.features?.suiteBuilderV2);
  const externalTestsEnabled = Boolean(user?.features?.externalTestSuites);

  // Test Runs (and agentic run detail) belong to the Suites world; everything
  // else under /runs is a Lab. External runs are rows in that very same
  // `/runs/test` table, so their detail pages belong here too — leaving them out
  // would switch the sub-nav to the Labs tabs the moment you opened one.
  const isSuitesGroup =
    location.pathname.startsWith('/runs/test') ||
    location.pathname.startsWith('/runs/tests') ||
    location.pathname.startsWith('/runs/external');

  const navConfig = React.useMemo<SubNavConfig>(() => {
    if (isSuitesGroup) {
      return {
        title: 'Suites',
        basePath: '/suites',
        items: [
          // Test Runs is GA and the default landing; the suite list/builder
          // tab ("Configuration") needs suite-builder.v2.
          { id: 'runs', label: 'Test Runs', icon: <Play size={16} />, to: '/runs/test' },
          ...(externalTestsEnabled
            ? [
                {
                  id: 'external-tests',
                  label: 'External Tests',
                  icon: <TestTube size={16} />,
                  to: '/runs/external-tests',
                },
              ]
            : []),
          ...(suitesEnabled
            ? [{ id: 'suites', label: 'Configuration', icon: <Stack size={16} />, to: '/suites' }]
            : []),
        ],
        // `/runs/external` belongs here for the same reason it is in
        // `isSuitesGroup` above — an external run is a row in that very
        // `/runs/test` table. Leaving it out rendered the Suites tabs with
        // nothing highlighted at all.
        getActiveId: ({ pathname }) =>
          pathname.startsWith('/runs/external-tests')
            ? 'external-tests'
            : pathname.startsWith('/runs/test') ||
                pathname.startsWith('/runs/tests') ||
                pathname.startsWith('/runs/external')
              ? 'runs'
              : pathname.startsWith('/suites')
                ? 'suites'
                : undefined,
      };
    }

    const items: SubNavConfig['items'] = [];
    if (qaRunsEnabled) {
      items.push({
        id: 'qa',
        label: 'QA Runs',
        icon: <MagnifyingGlass size={16} />,
        to: '/runs/qa',
      });
    }

    return {
      title: 'Labs',
      basePath: '/runs',
      items,
      // QA Runs is the only Labs tab; QA run detail URLs are /runs/$propertyId/$runId.
      getActiveId: ({ pathname }) => (pathname.startsWith('/runs') ? 'qa' : undefined),
    };
  }, [
    isSuitesGroup,
    qaRunsEnabled,
    suitesEnabled,
    externalTestsEnabled,
  ]);

  useSubNav(navConfig);

  return <Outlet />;
}
